import React from "react";
import { useTranslation } from "react-i18next";

const STEPS = [
  { id: "step1", icon: "fas fa-walking" },
  { id: "step2", icon: "fas fa-clock" },
  { id: "step3", icon: "fas fa-gavel" },
  { id: "step4", icon: "fas fa-trophy" },
];

function ProcessSection() {
  const { t } = useTranslation();
  const imageSrc = `${process.env.PUBLIC_URL || ""}/images/athlete_at_competition.png`;

  return (
    <section
      className="relative z-10 bg-white pb-14 pt-12 transition-colors dark:bg-slate-800 md:pb-16 md:pt-14"
      id="process-section"
      aria-labelledby="taolu-process-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center md:mb-12">
          <h2
            id="taolu-process-heading"
            className="relative mb-5 inline-block text-4xl font-semibold tracking-tight text-primary dark:text-primary-light md:text-5xl"
          >
            {t("taolu_process.title")}
            <span
              className="absolute -bottom-2.5 left-1/2 h-0.5 w-16 -translate-x-1/2 rounded-full bg-primary dark:bg-primary-light"
              aria-hidden
            />
          </h2>
        </header>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-stretch lg:gap-12 xl:gap-14">
          <div className="order-2 flex min-h-0 flex-col lg:order-1">
            <ol
              className="m-0 flex list-none flex-col gap-4 p-0 sm:gap-5"
              aria-label={t("taolu_process.timeline_label")}
            >
              {STEPS.map((step, index) => {
                const n = index + 1;
                const text = t(`taolu_process.${step.id}`);
                const stepLabel = t("taolu_process.step_label", { n });
                const headingId = `process-${step.id}-title`;

                return (
                  <li key={step.id} className="min-h-0">
                    <article
                      className="group relative h-full w-full overflow-hidden rounded-2xl border border-slate-200 border-l-4 border-l-slate-300 bg-white shadow-sm transition-all dark:border-slate-700 dark:border-l-slate-600 dark:bg-slate-800 motion-safe:hover:shadow-sm"
                      aria-labelledby={headingId}
                    >
                      <span
                        className="pointer-events-none absolute right-3 top-2 select-none text-4xl font-black tabular-nums leading-none text-primary/[0.09] dark:text-primary-light/[0.11] sm:right-4 sm:top-2.5 sm:text-5xl md:text-6xl"
                        aria-hidden
                      >
                        {n}
                      </span>
                      <div className="relative z-[1] px-4 pb-4 pt-4 pr-[2.85rem] sm:px-5 sm:pb-5 sm:pt-5 sm:pr-16 md:pr-[4.25rem]">
                        <div className="flex flex-col gap-3">
                          <div
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 transition-transform duration-300 dark:bg-primary-light/15 sm:h-12 sm:w-12 md:h-14 md:w-16 motion-safe:group-hover:scale-105"
                            aria-hidden
                          >
                            <i
                              className={`${step.icon} text-lg text-primary dark:text-primary-light md:text-xl lg:text-2xl`}
                            />
                          </div>
                          <div className="min-w-0 text-left">
                            <p
                              id={headingId}
                              className="text-xs font-semibold uppercase tracking-wide text-primary dark:text-primary-light"
                            >
                              {stepLabel}
                            </p>
                            <p className="mt-2 text-pretty text-base leading-relaxed text-slate-700 dark:text-slate-300">
                              {text}
                            </p>
                          </div>
                        </div>
                      </div>
                    </article>
                  </li>
                );
              })}
            </ol>

            <aside
              className="mt-8 overflow-hidden rounded-2xl border border-primary/20 border-l-4 border-l-primary bg-gradient-to-br from-primary/[0.07] to-primary/[0.03] shadow-md ring-1 ring-primary/10 dark:border-primary-light/25 dark:border-l-primary-light dark:from-primary-light/[0.12] dark:to-primary-light/[0.05] dark:ring-primary-light/15 sm:mt-10"
              aria-label={t("taolu_process.conclusion_label")}
            >
              <div className="flex items-start gap-4 px-5 py-5 sm:gap-5 sm:px-6 sm:py-6">
                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/90 shadow-sm dark:bg-slate-800/90 sm:h-12 sm:w-12"
                  aria-hidden
                >
                  <i className="fas fa-lightbulb text-lg text-primary dark:text-primary-light sm:text-xl" />
                </div>
                <p className="min-w-0 flex-1 text-pretty text-sm font-medium leading-relaxed text-slate-800 dark:text-slate-100 sm:text-base">
                  {t("taolu_process.conclusion")}
                </p>
              </div>
            </aside>
          </div>

          <figure className="order-1 lg:order-2 lg:flex lg:min-h-0 lg:h-full">
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm dark:border-slate-700 dark:bg-slate-900 lg:aspect-auto lg:min-h-0 lg:flex-1">
              <img
                src={imageSrc}
                alt={t("taolu_process.image_alt")}
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          </figure>
        </div>
      </div>
    </section>
  );
}

export default ProcessSection;
